import type { AwsCredentialIdentity } from "@aws-sdk/types";
import { getEnvironmentConfig } from "../configurations/config";
import { MidwayIdentityCredentialProviderSTS } from "@amzn/midway-identity-credential-provider";

interface MidwayIdentityCredentialSTS {
  AccessKeyId?: string;
  SecretAccessKey?: string;
  SessionToken?: string;
  Expiration?: Date;
  MidwayUser?: string;
}

const CREDENTIAL_REFRESH_WINDOW_MS = 5 * 60 * 1000;

let credentialProvider: MidwayIdentityCredentialProviderSTS | undefined;
let awsCredentials: MidwayIdentityCredentialSTS | undefined;
let credentialProviderKey: string | undefined;

const hasValidCachedCredentials = (
  credentials?: MidwayIdentityCredentialSTS
): boolean => {
  if (!credentials?.AccessKeyId || !credentials?.SecretAccessKey) {
    return false;
  }

  if (!credentials.Expiration) {
    return true;
  }

  return (
    credentials.Expiration.getTime() - Date.now() > CREDENTIAL_REFRESH_WINDOW_MS
  );
};

/**
 * Get Midway credentials
 * @returns AWS credentials
 */
export const getMidwayCredentials = async (): Promise<AwsCredentialIdentity> => {
  const { AccessKeyId, SecretAccessKey, SessionToken, Expiration } =
    await fetchTemporarySigV4Credentials();

  if (!AccessKeyId || !SecretAccessKey) {
    throw new Error("Invalid credentials");
  }

  return {
    accessKeyId: AccessKeyId,
    secretAccessKey: SecretAccessKey,
    sessionToken: SessionToken,
    expiration: Expiration,
  };
};

/**
 * Get user alias from Midway
 * @returns alias
 */
export const getUserAlias = async (): Promise<string> => {
  const awsCredentials = await fetchTemporarySigV4Credentials();
  return awsCredentials.MidwayUser || '';
};

/**
 * Get clean username from Midway user alias
 * Extracts "lculotta" from "amazonfederatemidway_lculotta"
 * @returns clean username
 */
export const getCleanUsername = async (): Promise<string> => {
  const fullAlias = await getUserAlias();
  
  if (fullAlias && fullAlias.startsWith('amazonfederatemidway_')) {
    return fullAlias.replace('amazonfederatemidway_', '');
  }
  
  return fullAlias || 'user';
};

export const fetchTemporarySigV4Credentials = async (): Promise<MidwayIdentityCredentialSTS> => {
  if (hasValidCachedCredentials(awsCredentials)) {
    return awsCredentials as MidwayIdentityCredentialSTS;
  }

  const config = getEnvironmentConfig();
  
  // Match the reference implementation exactly - use window.location.href
  // This should match what the working reference packages use
  const clientId = new URL(window.location.href);

  const providerKey = `${config.midwayRoleArn}|${config.partition}|${config.region}|${clientId.origin}`;
  if (!credentialProvider || credentialProviderKey !== providerKey) {
    credentialProvider = new MidwayIdentityCredentialProviderSTS(
      config.midwayRoleArn,
      clientId,
      clientId,
      config.partition,
      {
        maxAttempts: 3,
        region: config.region,
      }
    );
    credentialProviderKey = providerKey;
  }

  awsCredentials = await credentialProvider.getCredentials();
  return awsCredentials as MidwayIdentityCredentialSTS;
};
